import { loadStripe, Stripe, StripeElements } from '@stripe/stripe-js';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';
import { BillingAddress } from '@/app/pages/onboarding/components/StripePaymentForm';

/**
 * Stripe configuration with product and price IDs
 */
const STRIPE_CONFIG = {
  products: {
    starter: {
      name: 'Starter Plan',
      productId: 'prod_SzoTxFWphjbtPZ',
      priceId: 'price_1S3oqIS0YjtcmQFnddc0B3n8',
      basePrice: 99
    },
    business: {
      name: 'Business Plan',
      productId: 'prod_SzoXjxEVZ2d32C',
      priceId: 'price_1S3ou6S0YjtcmQFnEk4PMMWp',
      basePrice: 1800
    },
    enterprise: {
      name: 'Enterprise Plan',
      productId: 'prod_SzoXr2RPzlLCPv',
      priceId: 'price_1S3ouVS0YjtcmQFnyZim5tEC',
      basePrice: 3500 // $3500/month for immediate access
    },
    disp: {
      name: 'DISP Addon',
      productId: 'prod_SzoYwrM0zYPTDD',
      priceId: 'price_1S3ov4S0YjtcmQFnblmXMP3L',
      price: 199
    },
    ismRffr: {
      name: 'ISM/RFFR Addon',
      productId: 'prod_SzoYASeXTu9CsN',  
      priceId: 'price_1S3ovhS0YjtcmQFndPAMG0y0',
      price: 3500
    }
  }
} as const;

/**
 * Payment line item for Stripe
 */
export interface PaymentLineItem {
  priceId: string;
  quantity: number;
}

/**
 * Payment addon details
 */
export interface PaymentAddon {
  name: string;
  price: number;
  priceId: string;
}

/**
 * Payment calculation result
 */
export interface PaymentCalculation {
  subtotal: number;
  addons: PaymentAddon[];
  gst: number;
  total: number;
  lineItems: PaymentLineItem[];
}

/**
 * GST rate for Australian customers (10%)
 */
const GST_RATE = 0.10;

/**
 * Payment processing result
 */
export interface PaymentResult {
  success: boolean;
  clientSecret?: string;
  paymentIntentId?: string;
  customerId?: string;
  error?: string;
}

/**
 * Payment processing data
 */
export interface PaymentData {
  companyPlan: string;
  selectedFrameworks: string[];
  customerEmail: string;
  customerName: string;
  saveCard: boolean;
  enterpriseChoice?: string; // 'meeting' or 'payment'
  billingAddress: BillingAddress;
  taxId: string; // Australian Business Number (ABN)
}

/**
 * Stripe payment service for handling payments and subscriptions
 */
export class StripePaymentService {
  private stripe: Promise<Stripe | null>;

  constructor() {
    this.stripe = loadStripe(import.meta.env.VITE_STRIPE_KEY);
  }

  /**
   * Calculate total payment amount and line items
   */
  calculatePayment(companyPlan: string, selectedFrameworks: string[]): PaymentCalculation {
    // Get the plan details (all plans now have basePrice)
    const planConfig = STRIPE_CONFIG.products[companyPlan as 'starter' | 'business' | 'enterprise'];
    if (!planConfig || !('basePrice' in planConfig)) {
      throw new Error(`Invalid plan: ${companyPlan}`);
    }

    const calculation: PaymentCalculation = {
      subtotal: planConfig.basePrice,
      addons: [],
      gst: 0,
      total: planConfig.basePrice,
      lineItems: [
        {
          priceId: planConfig.priceId,
          quantity: 1
        }
      ]
    };

    // Add framework addons
    selectedFrameworks?.forEach(frameworkId => {
      if (frameworkId === 'disp') {
        const addon = STRIPE_CONFIG.products.disp;
        calculation.addons.push({
          name: addon.name,
          price: addon.price,
          priceId: addon.priceId
        });
        calculation.subtotal += addon.price;
        calculation.lineItems.push({
          priceId: addon.priceId,
          quantity: 1
        });
      } else if (frameworkId === 'ism' || frameworkId === 'rffr') {
        // Check if ISM/RFFR addon is already added
        const existingAddon = calculation.addons.find(addon => addon.priceId === STRIPE_CONFIG.products.ismRffr.priceId);
        if (!existingAddon) {
          const addon = STRIPE_CONFIG.products.ismRffr;
          calculation.addons.push({
            name: addon.name,
            price: addon.price,
            priceId: addon.priceId
          });
          calculation.subtotal += addon.price;
          calculation.lineItems.push({
            priceId: addon.priceId,
            quantity: 1
          });
        }
      }
    });

    // Calculate GST (10% for Australian customers)
    calculation.gst = Math.round(calculation.subtotal * GST_RATE * 100) / 100;
    calculation.total = calculation.subtotal + calculation.gst;

    return calculation;
  }

  /**
   * Create payment intent and process payment
   */
  async processPayment(
    paymentData: PaymentData, 
    elements: StripeElements,
    stripeInstance: Stripe
  ): Promise<PaymentResult> {
    try {
      if (!stripeInstance) {
        throw new Error('Stripe instance not provided');
      }

      const calculation = this.calculatePayment(paymentData.companyPlan, paymentData.selectedFrameworks);
      
      // Enterprise plans with meeting choice skip payment processing
      if (paymentData.companyPlan === 'enterprise' && paymentData.enterpriseChoice === 'meeting') {
        return {
          success: true,
          error: 'Enterprise plan - meeting scheduled, no payment required'
        };
      }

      // Create payment intent via internal API (unauthenticated - onboarding flow)
      // Amount includes GST (10% for Australian customers)
      const paymentResponse = await deiterateAPI.POST('/deiterateInternalAPI/payment/create-intent', {
        body: JSON.stringify({
          lineItems: calculation.lineItems,
          customerEmail: paymentData.customerEmail,
          customerName: paymentData.customerName,
          saveCard: paymentData.saveCard,
          amount: Math.round(calculation.total * 100), // Convert to cents (including GST)
          subtotal: Math.round(calculation.subtotal * 100), // Subtotal in cents (before GST)
          gst: Math.round(calculation.gst * 100), // GST in cents
          billingAddress: paymentData.billingAddress,
          taxId: paymentData.taxId,
        })
      }, false); // requireAuth = false for onboarding

      const { clientSecret, customerId } = paymentResponse;

      // Get card element
      const cardElement = elements.getElement('card');
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Confirm payment
      const { error, paymentIntent } = await stripeInstance.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: paymentData.customerName,
            email: paymentData.customerEmail,
            address: {
              line1: paymentData.billingAddress.line1,
              line2: paymentData.billingAddress.line2 || undefined,
              city: paymentData.billingAddress.city,
              state: paymentData.billingAddress.state,
              postal_code: paymentData.billingAddress.postal_code,
              country: paymentData.billingAddress.country,
            },
          },
        },
      });

      if (error) {
        return {
          success: false,
          error: error.message
        };
      }

      return {
        success: true,
        clientSecret,
        paymentIntentId: paymentIntent?.id,
        customerId
      };

    } catch (error) {
      console.error('Payment processing error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Payment processing failed'
      };
    }
  }

  /**
   * Get formatted price display
   */
  getFormattedPrice(amount: number): string {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(amount);
  }

  /**
   * Get plan details by ID
   */
  getPlanDetails(planId: string) {
    return STRIPE_CONFIG.products[planId as keyof typeof STRIPE_CONFIG.products];
  }
}

// Export singleton instance
export const stripePaymentService = new StripePaymentService();
